import { FullscreenDialog } from '@/components/DesignSystem';
import { ToastMessages } from '@/constants/constant';
import { QueryKeys } from '@/constants/QueryKeys';
import { omitUnchangedFields } from '@/pages/Promotions/PromotionModal/Helpers';
import {
  PromotionInputs,
  PromotionModal,
} from '@/pages/Promotions/PromotionModal/PromotionModal';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider';
import { DetailedApiError } from '@/utilities/api/DetailedApiError';
import { PromotionApi } from '@/utilities/api/PromotionApi';
import { CircularProgress } from '@mui/material';
import { Stack } from '@mui/system';
import React from 'react';
import { useMutation, useQueryClient, useQuery } from 'react-query';

interface EditPromotionModalProps {
  onClose: () => void,
  promotionId: string,
}

export const EditPromotionModal = ({
  onClose,
  promotionId,
}: EditPromotionModalProps) => {
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();

  const { data: promotion, isLoading } = useQuery(
    [QueryKeys.GET_PROMOTION, promotionId],
    () => PromotionApi.get(promotionId),
  );

  const { mutate: savePromotion, isLoading: isSaving } = useMutation(
    (updates: PromotionInputs) => PromotionApi.update(
      promotionId,
      omitUnchangedFields({
        allLocations: updates.allLocations,
        description: updates.description ?? '',
        dollarCap: updates.dollarCap,
        endDate: updates.endDate ?? undefined,
        locationIds: updates.locationIds,
        name: updates.name,
        pointCap: updates.pointCap,
        pointsExpirationDate: updates.pointsExpirationDate ?? undefined,
        productIdsForEarningPoints: updates.productIdsForEarningPoints,
        productIdsForRedeemingPoints: updates.productIdsForRedeemingPoints,
        showInDashboardCarousel: updates.showInDashboardCarousel,
        showInDashboardTable: updates.showInDashboardTable,
      }, promotion),
    ),
    {
      onError: (error: DetailedApiError) => {
        openSnackbar(error.message);
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries([QueryKeys.GET_PROMOTIONS]);
        await queryClient.invalidateQueries([QueryKeys.GET_PROMOTION, promotionId]);
        openSnackbar(ToastMessages.promotionUpdated);
        onClose();
      },
    },
  );

  if (isLoading) {
    return (
      <FullscreenDialog
        onClose={onClose}
        open
        testID="edit-promotion-modal-loading-id"
      >
        <Stack alignItems="center">
          <CircularProgress />
        </Stack>
      </FullscreenDialog>
    );
  }

  if (!promotion) {
    return null;
  }

  return (
    <PromotionModal
      isSaving={isSaving}
      onClose={onClose}
      onSave={async (updates) => {
        savePromotion(updates);
      }}
      promotionToEdit={promotion}
      show
    />
  );
};
